import { memo, useEffect, useRef } from 'react';
import { WorldMap, WorldRegion } from 'src/modules/experts/components/WorldMap';

export interface InteractiveWorldMapProps {
  availableCountries?: string[];
  availableRegions?: WorldRegion[];
  activeCountries?: (string | undefined)[];
  activeRegions?: (WorldRegion | undefined)[];
  onCountryClick?: (value: { slug: string; region: string }) => void;
}

export const InteractiveWorldMap = memo(function InteractiveWorldMap({
  availableCountries = [],
  availableRegions = [],
  activeRegions = [],
  activeCountries = [],
  onCountryClick,
}: InteractiveWorldMapProps) {
  const mapRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const map = mapRef.current;

    if (!map) {
      return;
    }

    const onClickListener = (e: MouseEvent) => {
      const target = e.target as SVGPathElement;

      onCountryClick?.({
        slug: String(target.id),
        region: String(target.dataset.region),
      });
    };

    const allCountries = map.querySelectorAll<HTMLElement>('[id]');

    allCountries.forEach((country) => {
      country.classList.remove('available');
      country.classList.remove('active');

      const region = country.dataset.region as WorldRegion;

      if (
        availableCountries.includes(country.id) ||
        availableRegions.includes(region)
      ) {
        country.classList.add('available');

        if (
          activeCountries.includes(country.id) ||
          activeRegions.includes(region)
        ) {
          country.classList.add('active');
        }

        country.addEventListener('click', onClickListener);
      }
    });

    return () => {
      allCountries.forEach((country) => {
        country.removeEventListener('click', onClickListener);
      });
    };
  }, [
    availableCountries,
    availableRegions,
    activeRegions,
    activeCountries,
    onCountryClick,
  ]);

  return <WorldMap innerRef={mapRef} />;
});
